// AGC: Pure, side-effect-free functions that convert analytics stats into plain-language insights.
// AGC: Each function accepts a stats array (from analyticsQueryService) and returns an Insight.
// AGC: V1: English-only. i18n-aware generation is planned for V2.
import type {
  FeatureUsageRow,
  EntityCreationRow,
  CreationSourceRow,
  VoiceFrictionRow,
  VoiceErrorRow,
  WizardAbandonRow,
  FormFailureRow,
  OverviewStats,
} from './analyticsQueryService'

// ---------------------------------------------------------------------------
// AGC: Public types
// ---------------------------------------------------------------------------

export interface Insight {
  text: string
  severity: 'good' | 'neutral' | 'warn' | 'bad'
}

// ---------------------------------------------------------------------------
// AGC: Insight functions
// ---------------------------------------------------------------------------

/** AGC: High-level interpretation of overview stats — error rate and top feature. */
export function interpretOverview(stats: OverviewStats): Insight {
  if (stats.totalEvents === 0) {
    return { text: 'No activity in this period yet.', severity: 'neutral' }
  }

  const pct = stats.errorRatePct

  if (pct > 10) {
    return {
      text: `Error rate is ${pct}% — above the 10% healthy threshold. Check the "What's broken" section below.`,
      severity: 'bad',
    }
  }

  if (pct > 5) {
    return {
      text: `Error rate is ${pct}%. Keep an eye on it.`,
      severity: 'warn',
    }
  }

  if (stats.topFeature) {
    return {
      text: `Top feature: "${stats.topFeature.name}" used ${stats.topFeature.count} times. Healthy error rate (${pct}%).`,
      severity: 'good',
    }
  }

  return {
    text: `${stats.totalEvents} events recorded. No clear top feature yet.`,
    severity: 'neutral',
  }
}

/** AGC: Insight on feature usage distribution — dominant feature or spread. */
export function interpretTopFeatures(rows: FeatureUsageRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No feature_used events yet.', severity: 'neutral' }
  }

  const top = rows[0]!

  // AGC: If the top feature has 3× or more than the second, highlight dominance
  if (rows.length >= 2) {
    const second = rows[1]!
    if (second.count > 0 && top.count >= second.count * 3) {
      return {
        text: `"${top.feature_name}" dominates usage (${top.count}x) — consider promoting it as a headline feature.`,
        severity: 'good',
      }
    }
  } else {
    // AGC: Only one feature — single-feature dominance by default
    return {
      text: `"${top.feature_name}" dominates usage (${top.count}x) — consider promoting it as a headline feature.`,
      severity: 'good',
    }
  }

  const top3 = rows.slice(0, 3)
  const summary = top3.map((r) => `${r.feature_name} (${r.count})`).join(', ')
  return { text: `Top features: ${summary}.`, severity: 'neutral' }
}

/** AGC: Insight on where entities are being created from (voice, form, wizard, etc.). */
export function interpretCreationSources(rows: CreationSourceRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No entity creations yet.', severity: 'neutral' }
  }

  const total = rows.reduce((sum, r) => sum + r.count, 0)
  const leader = rows[0]!
  const leaderPct = Math.round((leader.count / total) * 100)

  if (leaderPct > 70) {
    return {
      text: `${leader.source} is the dominant creation channel (${leaderPct}% of all entities). Double down on it.`,
      severity: 'good',
    }
  }

  // AGC: Spread across sources — show the breakdown
  const breakdown = rows
    .map((r) => `${r.source} ${Math.round((r.count / total) * 100)}%`)
    .join(', ')
  return { text: `Entities are split across sources: ${breakdown}.`, severity: 'neutral' }
}

/** AGC: Insight on which entity types are most and least created. */
export function interpretEntityCreations(rows: EntityCreationRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No entities created yet.', severity: 'neutral' }
  }

  // AGC: rows arrive sorted desc — top is first, bottom is last
  const top = rows[0]!
  const bottom = rows[rows.length - 1]!
  return {
    text: `Most-created entity: ${top.entity_type} (${top.count}). Least: ${bottom.entity_type} (${bottom.count}).`,
    severity: 'neutral',
  }
}

/** AGC: Insight on voice recording retry friction — worst entity type surfaced first. */
export function interpretVoiceFriction(rows: VoiceFrictionRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No voice activity yet.', severity: 'neutral' }
  }

  // AGC: Find the entity type with the worst retry rate that has meaningful volume (≥3 successes)
  const significant = rows.filter((r) => r.firstTrySuccess + r.retries >= 3)
  const worst = significant.length > 0 ? significant[0]! : rows[0]!

  if (worst.retryRatePct > 30) {
    return {
      text: `Voice friction: "${worst.entity_type}" has ${worst.retryRatePct}% retry rate. This is your #1 fix.`,
      severity: 'bad',
    }
  }

  if (worst.retryRatePct > 15) {
    return {
      text: `Voice friction: "${worst.entity_type}" has ${worst.retryRatePct}% retry rate. Worth investigating.`,
      severity: 'warn',
    }
  }

  return {
    text: `Voice retry rates are healthy across all entity types (worst: ${worst.entity_type} at ${worst.retryRatePct}%).`,
    severity: 'good',
  }
}

// AGC: Per-error-code actionable advice for voice session failures
const VOICE_ERROR_ADVICE: Record<string, string> = {
  mic_denied: 'Review how/when you ask for mic permission.',
  parse_failed: "Users spoke but the AI couldn't understand — update voice prompts.",
  no_speech: 'Users tapped but didn\'t speak — review UX timing.',
  network_error: 'Backend connectivity issue.',
  user_canceled: 'Users canceled mid-recording — UX may be unclear.',
  mic_busy: 'Another app held the mic — consider coaching users.',
  transcribe_failed: 'Backend transcription is failing — check Whisper logs.',
}

/** AGC: Insight on the most common voice error code with actionable advice. */
export function interpretVoiceErrors(rows: VoiceErrorRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No voice failures.', severity: 'good' }
  }

  const total = rows.reduce((sum, r) => sum + r.count, 0)
  const top = rows[0]!
  const advice = VOICE_ERROR_ADVICE[top.error_code] ?? 'Investigate this error code in the logs.'

  return {
    text: `Top voice failure: "${top.error_code}" (${top.count} of ${total}). ${advice}`,
    severity: 'warn',
  }
}

/** AGC: Insight on the wizard step where most users drop off. */
export function interpretWizardAbandons(rows: WizardAbandonRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No wizard abandons.', severity: 'good' }
  }

  const top = rows[0]!
  return {
    text: `Biggest wizard leak: ${top.wizard_name} → step "${top.last_step_name}" (${top.count} drop-offs). Redesign this step.`,
    severity: 'warn',
  }
}

/** AGC: Insight on the most common form + error combination. */
export function interpretFormFailures(rows: FormFailureRow[]): Insight {
  if (rows.length === 0) {
    return { text: 'No form failures.', severity: 'good' }
  }

  const top = rows[0]!
  return {
    text: `Most failing form: "${top.form_name}" failing with "${top.error_code}" (${top.count}). Fix this error type first.`,
    severity: 'warn',
  }
}
